import React, {useState} from 'react';
import {curry} from 'ramda';
import {FrameRate} from '@accurate-player/accurate-player-core';

import {OnStringSetState} from '@pluto/@types';
import {TimelineMarker} from 'VideoAudioQC/Timeline/TimelineTypes';
import Table, {AdBreakData} from './Table';

export type OnButtonClick = (data: AdBreakData) => void;
export type OnStartFrameChange = (data: AdBreakData, start: number) => void;

export interface TableContainerProps {
    markers: TimelineMarker[];
    selectedMarkerId: string;
    adOnPlayingFrame?: TimelineMarker;
    frameRate: FrameRate;
    dropFrame: boolean;
    duration: number;
    onMarkerClick: (marker: AdBreakData) => void;
    onPreviewAdClick: OnButtonClick;
    onMoveClick: OnButtonClick;
    onGoToClick: OnButtonClick;
    onDeleteClick: OnButtonClick;
    onStartFrameChange: OnStartFrameChange;
    jobDeliveryDate: string;
}

export type OnRowClickProps = {
    onMarkerClick: (marker: AdBreakData) => void,
    setSelectedColumn: OnStringSetState,
}

export const onRowClick = curry((props: OnRowClickProps, event: MouseEvent, item: AdBreakData, column: string) => {
    const {
        onMarkerClick,
        setSelectedColumn,
    } = props;
    
    event.stopPropagation();
    event.preventDefault();
    
    onMarkerClick(item);
    setSelectedColumn(column);
});

export const onButtonClick = curry((click: OnButtonClick, data: AdBreakData) => {
    click(data);
});

export const stopPropagation = (event: React.MouseEvent) => { event.stopPropagation(); };

export default function TableContainer(props: TableContainerProps) {
    const {
        markers,
        selectedMarkerId,
        adOnPlayingFrame,
        frameRate,
        dropFrame,
        duration,
        onMarkerClick,
        onPreviewAdClick,
        onMoveClick,
        onGoToClick,
        onDeleteClick,
        onStartFrameChange,
        jobDeliveryDate,
    } = props;
    const [selectedColumn, setSelectedColumn] = useState('');
    
    const handleRowClick = onRowClick({
        onMarkerClick,
        setSelectedColumn,
    });
    const handlePreviewClick = onButtonClick(onPreviewAdClick);
    const handleMoveClick = onButtonClick(onMoveClick);
    const handleGoToClick = onButtonClick(onGoToClick);
    const handleDeleteClick = onButtonClick(onDeleteClick);
    
    return (
        <Table
            jobDeliveryDate={jobDeliveryDate}
            markers={markers}
            selectedMarkerId={selectedMarkerId}
            adOnPlayingFrame={adOnPlayingFrame}
            selectedColumn={selectedColumn}
            frameRate={frameRate}
            dropFrame={dropFrame}
            duration={duration}
            onRowClick={handleRowClick}
            onInputClick={stopPropagation}
            onPreviewClick={handlePreviewClick}
            onMoveClick={handleMoveClick}
            onGoToClick={handleGoToClick}
            onDeleteClick={handleDeleteClick}
            onStartFrameChange={onStartFrameChange}
        />
    );
}
